/**
 * The item editor's split controls (shares, percent, amounts) read and write the same
 * "Uneven split" text the engine already understands ("Emily 2, Chloe 1", "Emily $10"), so
 * nothing about stored items changes.
 */
import { parseCustomSplit } from "./split.ts";
import type { Person } from "../types.ts";

export type SplitUiMode = "even" | "shares" | "percent" | "amounts";

export interface SplitUi {
  mode: SplitUiMode;
  /** person id -> share count (shares mode) */
  shares: Record<string, number>;
  /** person id -> percent (percent mode) */
  percents: Record<string, number>;
  /** person id -> fixed cents (amounts mode); people missing here split what's left */
  amounts: Record<string, number>;
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const num = (n: number) => String(round2(n)).replace(/\.0+$/, "");

/** Works out which control matches an existing split text. */
export function readSplitUi(
  text: string,
  ticked: readonly Person[],
  everyone: readonly Person[],
): SplitUi {
  const empty: SplitUi = { mode: "even", shares: {}, percents: {}, amounts: {} };
  const parsed = parseCustomSplit(text, ticked, everyone);
  if (parsed.mode === "even" || parsed.errors.length) return empty;
  if (parsed.mode === "dollars") {
    return { ...empty, mode: "amounts", amounts: Object.fromEntries(parsed.values) };
  }
  // Weights: whole numbers read as shares; weights that add up to 100 read as percents.
  const weights = ticked.map((p) => (parsed.values.get(p.id) ?? 1000) / 1000);
  const sum = weights.reduce((a, b) => a + b, 0);
  const whole = weights.every((w) => Number.isInteger(w));
  if (Math.abs(sum - 100) < 0.05 && !(whole && weights.every((w) => w <= 20))) {
    return {
      ...empty,
      mode: "percent",
      percents: Object.fromEntries(ticked.map((p, i) => [p.id, weights[i]!])),
    };
  }
  return {
    ...empty,
    mode: "shares",
    shares: Object.fromEntries(ticked.map((p, i) => [p.id, weights[i]!])),
  };
}

/** Builds the split text for a control's values (empty text = even split). */
export function writeSplitUi(ui: SplitUi, ticked: readonly Person[]): string {
  if (ui.mode === "even" || ticked.length === 0) return "";
  if (ui.mode === "amounts") {
    return ticked
      .filter((p) => ui.amounts[p.id] !== undefined && ui.amounts[p.id]! >= 0)
      .map((p) => `${p.name} $${(ui.amounts[p.id]! / 100).toFixed(2)}`)
      .join(", ");
  }
  const values = ticked.map((p) =>
    ui.mode === "shares" ? (ui.shares[p.id] ?? 1) : (ui.percents[p.id] ?? 0),
  );
  // Everyone the same (1, 1, 1 or 50%, 50%) is just an even split.
  if (values.every((v) => v === values[0]) && values[0]! > 0) return "";
  return ticked.map((p, i) => `${p.name} ${num(values[i]!)}`).join(", ");
}

/** Equal percents that add up to exactly 100 (the last person absorbs the rounding). */
export function evenPercents(ticked: readonly Person[]): Record<string, number> {
  if (ticked.length === 0) return {};
  const each = round2(100 / ticked.length);
  const out: Record<string, number> = {};
  ticked.forEach(
    (p, i) =>
      (out[p.id] = i === ticked.length - 1 ? round2(100 - each * (ticked.length - 1)) : each),
  );
  return out;
}
